import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  MapPin,
  TrendingUp,
  Users,
  FileText,
  Home,
  Key,
  Phone,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FAQ from "@/components/common/FAQ";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

const sellerFaqs = [
  {
    question: "Was ist meine Immobilie im Kreis Mettmann wert?",
    answer:
      "Der Wert Ihrer Immobilie hängt von vielen Faktoren ab: Lage in der Stadt (z.B. Haan vs. Ratingen), Zustand, Baujahr, Wohnfläche und der aktuellen Marktlage. Eine fundierte Marktwertermittlung durch einen lokalen Experten ist der erste und wichtigste Schritt. Wir ermitteln den Marktwert Ihrer Immobilie kostenlos und unverbindlich.",
  },
  {
    question: "Wie lange dauert ein Immobilienverkauf im Kreis Mettmann?",
    answer:
      "Ein professionell begleiteter Immobilienverkauf dauert im Kreis Mettmann in der Regel zwischen 3 und 6 Monaten – von der Bewertung bis zum Notartermin. Mit der richtigen Preisstrategie und einer hochwertigen Vermarktung kann dieser Zeitraum oft deutlich verkürzt werden.",
  },
  {
    question: "Was kostet ein Immobilienmakler im Kreis Mettmann?",
    answer:
      "Seit der Maklerprovision-Reform 2020 teilen sich Käufer und Verkäufer die Provision. In NRW beträgt die ortsübliche Provision 3,57% inkl. MwSt. pro Seite. Im unverbindlichen Erstgespräch besprechen wir alle Details transparent.",
  },
  {
    question: "Wann ist der beste Zeitpunkt für den Immobilienverkauf?",
    answer:
      "Grundsätzlich gibt es im Kreis Mettmann das ganze Jahr über Nachfrage. Erfahrungsgemäß sind Frühjahr und Herbst besonders aktive Marktphasen. Wichtiger als der Zeitpunkt sind jedoch die richtige Preisstrategie und professionelle Vermarktung.",
  },
  {
    question: "Wie findet BRIMO Immobilien den richtigen Käufer?",
    answer:
      "Wir nutzen unser lokales Käufernetzwerk, alle relevanten Immobilienportale (ImmobilienScout24, Immowelt), Social Media und gezielte Direktansprache. Durch sorgfältige Käuferprüfung mit Bonitäts- und Finanzierungsnachweis stellen wir sicher, dass der Verkauf sicher abgewickelt wird.",
  },
  {
    question: "Was passiert beim Notartermin?",
    answer:
      "Beim Notartermin wird der Kaufvertrag beurkundet. Der Notar liest den Vertrag vor, erklärt alle Inhalte und beantwortet Fragen. Nach der Unterzeichnung beginnen die rechtlichen Schritte zur Eigentumsübertragung (Eintragung im Grundbuch). Wir begleiten Sie persönlich.",
  },
  {
    question: "Welche Unterlagen brauche ich für den Verkauf?",
    answer:
      "Typische Unterlagen: Grundbuchauszug, Lageplan, Grundrisse, Energieausweis, Baupläne, Teilungserklärung (bei ETW), Mietverträge (bei vermieteten Objekten). Wir unterstützen Sie bei der Zusammenstellung aller relevanten Dokumente.",
  },
];

const processSteps = [
  { num: "01", title: "Analyse", desc: "Kostenlose Besichtigung & Bestandsaufnahme Ihrer Immobilie" },
  { num: "02", title: "Bewertung", desc: "Fundierte Marktwertermittlung auf Basis lokaler Marktdaten" },
  { num: "03", title: "Vermarktung", desc: "Professionelles Exposé, Portalschaltung & Käufersuche" },
  { num: "04", title: "Käuferauswahl", desc: "Besichtigungen, Bonitätsprüfung & Verhandlung" },
  { num: "05", title: "Notarabschluss", desc: "Kaufvertragsvorbereitung & Begleitung zum Notartermin" },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Immobilie verkaufen im Kreis Mettmann",
  provider: { "@type": "RealEstateAgent", name: "BRIMO Immobilien NRW" },
  areaServed: "Kreis Mettmann",
  description:
    "Professionelle Begleitung beim Immobilienverkauf im Kreis Mettmann. Marktwertermittlung, Vermarktung und Käufersuche.",
};

export default function SellerPage() {
  useMeta(
    "Immobilie verkaufen im Kreis Mettmann | BRIMO Immobilien",
    "Immobilie verkaufen im Kreis Mettmann mit BRIMO Immobilien. Kostenlose Marktwertermittlung, professionelle Vermarktung und strukturierter Verkaufsprozess. Jetzt anfragen."
  );
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end pb-16" data-testid="seller-hero">
        <div className="absolute inset-0">
          <img
            src={cityConfig.images.sellerHero}
            alt="Immobilie verkaufen Kreis Mettmann"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <p className="text-gray-300 text-xs uppercase tracking-[0.3em] mb-4">
            Für Eigentümer im Kreis Mettmann
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            IMMOBILIE VERKAUFEN
            <br />
            <span className="text-[#E61D38]">IM KREIS METTMANN</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed mb-8">
            Wir unterstützen Eigentümer mit Marktanalyse, Vermarktungsstrategie und strukturiertem
            Verkaufsprozess – für optimale Ergebnisse im Kreis Mettmann.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/marktwertermittlung-kreis-mettmann"
              className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors"
              data-testid="seller-hero-cta"
            >
              Kostenlose Marktwertermittlung
              <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${cityConfig.phone}`}
              className="flex items-center gap-2 border border-white/30 text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:border-white/60 transition-colors"
              data-testid="seller-phone-cta"
            >
              <Phone size={14} />
              {cityConfig.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Problem Awareness */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="seller-problems">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-12"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            HÄUFIGE FRAGEN VON EIGENTÜMERN
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <TrendingUp className="text-[#E61D38]" size={28} />,
                q: "Was ist meine Immobilie wert?",
                a: "Eine professionelle Marktwertermittlung zeigt den realen Wert Ihrer Immobilie – ohne Unter- oder Überschätzung.",
              },
              {
                icon: <Home className="text-[#E61D38]" size={28} />,
                q: "Wann ist der richtige Zeitpunkt?",
                a: "Der Markt im Kreis Mettmann ist aktiv. Mit der richtigen Strategie erzielen Sie jederzeit einen optimalen Preis.",
              },
              {
                icon: <Users className="text-[#E61D38]" size={28} />,
                q: "Wie finde ich den richtigen Käufer?",
                a: "Durch unser Netzwerk, Portalpräsenz und sorgfältige Käuferprüfung finden wir den idealen Käufer für Ihre Immobilie.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#262626] border border-white/5 p-8 hover:border-[#E61D38]/30 transition-colors"
                data-testid={`problem-item-${i}`}
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-white font-bold text-base mb-3">{item.q}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="seller-services">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-12"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            UNSER SERVICE FÜR VERKÄUFER
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                icon: <TrendingUp size={24} className="text-[#E61D38]" />,
                title: "Marktwertermittlung",
                desc: "Fundierte Bewertung Ihrer Immobilie auf Basis aktueller Marktdaten und lokaler Expertise.",
              },
              {
                icon: <FileText size={24} className="text-[#E61D38]" />,
                title: "Vermarktungsstrategie",
                desc: "Professionelles Exposé, Fotografie und gezielte Präsentation auf allen relevanten Plattformen.",
              },
              {
                icon: <Users size={24} className="text-[#E61D38]" />,
                title: "Käuferprüfung",
                desc: "Sorgfältige Bonitätsprüfung und Finanzierungsnachweis – für einen sicheren Verkaufsabschluss.",
              },
              {
                icon: <Key size={24} className="text-[#E61D38]" />,
                title: "Abwicklungsbegleitung",
                desc: "Von der Kaufvertragsverhandlung bis zum Notartermin – wir sind an Ihrer Seite.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#262626] border border-white/5 p-7 hover:border-[#E61D38]/40 transition-all duration-300 group"
                data-testid={`service-item-${i}`}
              >
                <div className="mb-4 group-hover:scale-110 transition-transform origin-left">{item.icon}</div>
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-3"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Market */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="seller-local">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                DER IMMOBILIENMARKT IM KREIS METTMANN
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Der Kreis Mettmann bietet eine vielfältige Immobilienlandschaft – von lebendigen
                Städten wie Hilden und Ratingen bis zu ruhigen Wohnorten wie Haan und Wülfrath.
                Jede Stadt hat ihre eigene Preisentwicklung und Nachfragestruktur.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Als lokaler Makler kennen wir die aktuellen Verkaufspreise, die typischen
                Käuferprofile und die Besonderheiten jeder Stadt – für optimale
                Vermarktungsstrategie.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {cityConfig.districts.map((d) => (
                  <Link
                    key={d.slug}
                    to={`/immobilie-verkaufen-${d.slug}`}
                    className="flex items-center gap-2 text-gray-300 text-sm hover:text-[#E61D38] transition-colors group"
                    data-testid={`local-district-${d.slug}`}
                  >
                    <MapPin size={12} className="text-[#E61D38] flex-shrink-0" />
                    {d.name}
                    <ArrowRight
                      size={10}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </Link>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={cityConfig.images.shopFront}
                alt="BRIMO Immobilien NRW – Kreis Mettmann"
                className="w-full h-72 md:h-96 object-cover"
                loading="lazy"
              />
              <div className="absolute top-4 right-4 bg-[#E61D38] px-4 py-3">
                <p className="text-white font-bold text-xs uppercase tracking-wider">
                  Vor Ort im Kreis Mettmann
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="seller-process">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-12"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            DER VERKAUFSPROZESS
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
            {processSteps.map((step, i) => (
              <div
                key={i}
                className="relative"
                data-testid={`process-step-${i}`}
              >
                <div className="text-[#E61D38] font-black text-3xl mb-3"
                     style={{ fontFamily: "Montserrat, sans-serif" }}>
                  {step.num}
                </div>
                <div className="h-0.5 w-full bg-[#E61D38]/20 mb-4" />
                <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust – Vincenza */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="seller-trust">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                PERSÖNLICHE BEGLEITUNG DURCH EXPERTEN
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Beim Immobilienverkauf vertrauen Sie einer der wichtigsten finanziellen
                Entscheidungen Ihres Lebens in professionelle Hände. Janine Muth und das
                Team von BRIMO Immobilien NRW begleiten Sie mit Sachkenntnis,
                Marktkenntnis und persönlichem Engagement.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Lokale Marktkenntnis seit Jahren",
                  "Strukturierter Verkaufsprozess",
                  "Persönlicher Ansprechpartner",
                  "Diskret & professionell",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                    <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/kontakt"
                className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors w-fit"
                data-testid="seller-trust-cta"
              >
                Beratungsgespräch vereinbaren
                <ArrowRight size={14} />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src={cityConfig.manager.photo}
                  alt={`${cityConfig.manager.name} – BRIMO Immobilien NRW`}
                  className="w-full max-w-sm mx-auto h-auto object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-[#0d0d0d]/90 p-4">
                  <p className="text-white font-bold text-sm">{cityConfig.manager.name}</p>
                  <p className="text-[#E61D38] text-xs uppercase tracking-wider mt-0.5">
                    {cityConfig.manager.title}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ items={sellerFaqs} title="FAQ – Immobilie verkaufen im Kreis Mettmann" />

      {/* Lead Form */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="seller-form-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                MARKTWERT ANFRAGEN
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8 text-sm">
                Erfahren Sie kostenlos und unverbindlich, was Ihre Immobilie im Kreis Mettmann aktuell
                wert ist. Füllen Sie das Formular aus – wir melden uns innerhalb von 24 Stunden.
              </p>
              <div className="space-y-4">
                {[
                  "Kostenlose Erstbewertung",
                  "Unverbindliche Beratung",
                  "Persönlicher Ansprechpartner",
                  "Schnelle Rückmeldung",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-[#E61D38] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <LeadForm
                formType="seller"
                title="Marktwert Ihrer Immobilie"
                ctaText="Marktwert anfragen"
                showDistrict={true}
                showPropertyType={true}
                pageSource="/immobilie-verkaufen-kreis-mettmann"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
